import { DocumentIcon } from "@heroicons/react/24/outline";
import AudioFileMessageItemEntry from "./AudioFileMessageItemEntry";
import { FileData } from "../types/api";
import { getFirstDownloadUrl } from "./downloadUrlCache";
import PreviewFile, { PreviewFileProps } from "./PreviewFile";
import { useState } from "react";

export const FileMessageItemEntry = ({ file }: { file: FileData }) => {
  if (file.type === "audio") {
    return <AudioFileMessageItemEntry file={file} />;
  } else if (file.type === "image") {
    return <ImageFileMessageItemEntry file={file} />;
  } else if (file.type === "document") {
    return <DocumentFileMessageItemEntry file={file} />;
  } else {
    console.error("Unknown file type", file);
    return null;
  }
};

// Higher order component that makes the entire area clickable to show preview
export const withFilePreview = (Component: React.FC<{ file: FileData }>) => {
  return ({ file }: { file: FileData }) => {
    const [previewFile, setPreviewFile] = useState<
      PreviewFileProps["previewFile"] | null
    >(null);

    const handleClick = () => {
      setPreviewFile({
        url: getFirstDownloadUrl(file.download_url),
        type: file.type,
        contentType: file.content_type,
        name: file.name,
      });
    };

    return (
      <>
        <div onClick={handleClick} className="cursor-pointer">
          <Component file={file} />
        </div>
        {previewFile && (
          <PreviewFile
            previewFile={previewFile}
            closeFilePreview={() => setPreviewFile(null)}
          />
        )}
      </>
    );
  };
};

function formatFileSize(bytes: number) {
  if (bytes < 1024) return bytes + " B";
  else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  else return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export const ImageFileMessageItemEntry = withFilePreview(
  ({ file }: { file: FileData }) => {
    return (
      <div className="relative rounded bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200">
        <div className="flex items-center justify-center h-40 w-auto">
          <img
            src={getFirstDownloadUrl(file.download_url)}
            alt={file.name}
            className="max-h-40 object-contain"
          />
        </div>
      </div>
    );
  }
);

// Component for rendering document files
export const DocumentFileMessageItemEntry = withFilePreview(
  ({ file }: { file: FileData }) => {
    return (
      <div className="flex flex-col items-center justify-center h-24 w-32 p-4 bg-slate-100 rounded border border-slate-200 gap-1.5">
        <DocumentIcon className="h-5 w-5 text-gray-500" />
        <div className="text-sm text-center text-chat-text-light line-clamp-1 w-full pb-1">
          {file.name}
        </div>
        <div className="text-xs text-chat-text-light">
          {file.name.split(".").pop()?.toUpperCase() || "FILE"} ·{" "}
          {formatFileSize(file.bytesize)}
        </div>
      </div>
    );
  }
);

export const FileMessageItem = ({ files }: { files: FileData[] }) => {
  if (!files || files.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 bg-white overflow-hidden justify-end">
      {files.map((file) => (
        <FileMessageItemEntry key={file.id} file={file} />
      ))}
    </div>
  );
};
